import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import { Badge, NavLink, Paper, Skeleton, Table, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'

import { TAXONOMY_CATEGORIES, type TaxonomyCategory, rollbackTaxonomy } from '@/services/taxonomyAdminService'
import { listPrompts, rollbackPrompt, type PromptSummaryDTO } from '@/services/promptAdminService'
import { listBaselineVersions, type BaselineVersionDTO } from '@/services/riskBaselineAdminService'
import { PageHeader } from '@/components/page/PageHeader'
import { useAdminScope } from '../-admin/adminScope'
import { TaxonomyVersionList } from '../-admin/TaxonomyVersionList'
import { PromptVersionList } from '../-prompts/PromptVersionList'

export const Route = createFileRoute('/_app/organization/admin/versions')({
  head: () => ({ meta: [{ title: 'Versions · Admin · AERRIS' }] }),
  component: VersionsTab,
})

/** `tax:COMMERCIAL` | `prompt:process_contract` | `risk` — the selected versionable artifact. */
type Selection =
  | { kind: 'tax'; key: TaxonomyCategory }
  | { kind: 'prompt'; key: string }
  | { kind: 'risk'; key: 'baseline' }

function parseSel(sel: string | undefined, prompts: PromptSummaryDTO[]): Selection | null {
  if (!sel) return null
  if (sel === 'risk') return { kind: 'risk', key: 'baseline' }
  const [kind, ...rest] = sel.split(':')
  const key = rest.join(':')
  if (kind === 'tax' && (TAXONOMY_CATEGORIES as readonly string[]).includes(key)) {
    return { kind: 'tax', key: key as TaxonomyCategory }
  }
  if (kind === 'prompt' && prompts.some((p) => p.name === key)) return { kind: 'prompt', key }
  return null
}

function VersionsTab() {
  const { customerUuid, globalMode, clientId, canManage, scopeName, loading: scopeLoading } = useAdminScope()
  const sel = (useLocation().search as Record<string, string | undefined>).sel

  const [prompts, setPrompts] = useState<PromptSummaryDTO[]>([])
  const [promptsLoading, setPromptsLoading] = useState(true)
  const [selected, setSelected] = useState<Selection>({ kind: 'tax', key: 'COMMERCIAL' })
  const [refreshKey, setRefreshKey] = useState(0)

  const load = useCallback(async () => {
    if (scopeLoading) return
    setPromptsLoading(true)
    try {
      if (!canManage || (!globalMode && !clientId)) {
        setPrompts([])
        return
      }
      const list = await listPrompts(customerUuid)
      setPrompts([...list].sort((a, b) => a.name.localeCompare(b.name)))
    } catch (e) {
      console.error('Failed to load prompts:', e)
    } finally {
      setPromptsLoading(false)
    }
  }, [customerUuid, globalMode, clientId, canManage, scopeLoading])

  useEffect(() => {
    void load()
  }, [load])

  // Honour a ?sel=… deep link (from a tab's "View history →") once prompts are known.
  useEffect(() => {
    const parsed = parseSel(sel, prompts)
    if (parsed) setSelected(parsed)
  }, [sel, prompts])

  const promptVersionByName = useMemo(
    () => new Map(prompts.map((p) => [p.name, p.currentVersion])),
    [prompts],
  )

  const loading = scopeLoading || promptsLoading

  const onRollbackTaxonomy = async (category: TaxonomyCategory, toVersion: number) => {
    try {
      await rollbackTaxonomy(customerUuid, category, toVersion)
      notifications.show({ color: 'green', title: 'Rolled back', message: `${category} restored to v${toVersion} as a new version.` })
      setRefreshKey((k) => k + 1)
    } catch {
      notifications.show({ color: 'red', title: 'Rollback failed', message: 'The server rejected the rollback.' })
    }
  }

  const onRollbackPrompt = async (name: string, toVersion: number) => {
    try {
      await rollbackPrompt(customerUuid, name, toVersion)
      notifications.show({ color: 'green', title: 'Rolled back', message: `${name} restored to v${toVersion} as a new version.` })
      setRefreshKey((k) => k + 1)
    } catch {
      notifications.show({ color: 'red', title: 'Rollback failed', message: 'The server rejected the rollback.' })
    }
  }

  const header = (
    <PageHeader
      title="Versions"
      subtitle="Every published version of the taxonomy, the prompts and the risk baseline, with who published it and why."
    />
  )

  if (loading) {
    return (
      <div className="versions-page">
        {header}
        <div style={{ display: 'flex', gap: '1.5rem' }} aria-busy="true">
          <Skeleton height={360} width={240} radius="md" style={{ flexShrink: 0 }} />
          <Skeleton height={360} style={{ flex: 1 }} radius="md" />
        </div>
      </div>
    )
  }

  if (!canManage) {
    return (
      <div className="versions-page">
      {header}
      <div className="notice">
        <Lock size={16} />
        {globalMode
          ? 'Browsing the global default version history requires platform-admin access.'
          : !clientId
            ? 'Select a client to browse its version history.'
            : `You need admin access to ${scopeName} to view its version history.`}
      </div>
      </div>
    )
  }

  return (
    <div className="versions-page">
    {header}
    <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start' }}>
      <Paper withBorder radius="md" style={{ width: 240, flexShrink: 0, overflow: 'hidden' }}>
        <div className="admin-versions-group-label">Taxonomy</div>
        {TAXONOMY_CATEGORIES.map((c) => (
          <NavLink
            key={c}
            active={selected.kind === 'tax' && selected.key === c}
            onClick={() => setSelected({ kind: 'tax', key: c })}
            label={c}
          />
        ))}
        <div className="admin-versions-group-label">Risk</div>
        <NavLink
          active={selected.kind === 'risk'}
          onClick={() => setSelected({ kind: 'risk', key: 'baseline' })}
          label="Risk baseline"
        />
        <div className="admin-versions-group-label">Prompts</div>
        {prompts.map((p) => (
          <NavLink
            key={p.name}
            active={selected.kind === 'prompt' && selected.key === p.name}
            onClick={() => setSelected({ kind: 'prompt', key: p.name })}
            label={<span title={p.name} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{p.name}</span>}
            rightSection={
              <Badge size="xs" variant="light" color="gray">
                {promptVersionByName.get(p.name) != null ? `v${promptVersionByName.get(p.name)}` : '—'}
              </Badge>
            }
          />
        ))}
      </Paper>

      <div style={{ flex: 1, minWidth: 0 }}>
        <Text fw={600} mb="sm">
          {selected.kind === 'tax'
            ? `${selected.key} taxonomy`
            : selected.kind === 'risk'
              ? 'Risk baseline'
              : selected.key}{' '}
          — version history
        </Text>
        {selected.kind === 'risk' ? (
          // Keyed on the refresh counter so a rollback elsewhere starts it afresh.
          <RiskVersionHistory key={`risk:${customerUuid}:${refreshKey}`} customerUuid={customerUuid} />
        ) : selected.kind === 'tax' ? (
          <TaxonomyVersionList
            key={`tax:${selected.key}:${customerUuid}`}
            customerUuid={customerUuid}
            category={selected.key}
            canManage={canManage}
            refreshKey={refreshKey}
            onRollback={(v) => onRollbackTaxonomy(selected.key, v)}
          />
        ) : (
          <PromptVersionList
            key={`prompt:${selected.key}:${customerUuid}`}
            customerUuid={customerUuid}
            name={selected.key}
            canManage={canManage}
            refreshKey={refreshKey}
            onRollback={(v) => onRollbackPrompt(selected.key, v)}
          />
        )}
      </div>
    </div>
    </div>
  )
}

/**
 * The risk baseline's published versions, newest first. Compare and rollback live on the Risk
 * tab's own Versions view (they need the draft and the re-evaluation opt-in), so this list links
 * there rather than repeating them.
 */
function RiskVersionHistory({ customerUuid }: { customerUuid: string | null }) {
  const [versions, setVersions] = useState<BaselineVersionDTO[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    listBaselineVersions(customerUuid)
      .then((vs) => { if (!cancelled) setVersions([...vs].sort((a, b) => b.version - a.version)) })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [customerUuid])

  const riskLink = (
    <Link to="/organization/admin/risk" search={(prev) => ({ ...prev, tab: 'versions' })}>
      Compare or roll back on the Risk tab
    </Link>
  )

  if (failed) return <Text c="red" size="sm">Could not load the risk baseline versions.</Text>
  if (!versions) return <Skeleton height={160} radius="md" />
  if (versions.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No risk baseline has been published in this scope yet. {riskLink}
      </Text>
    )
  }
  return (
    <div className="page-card taxonomy-table-wrap">
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Version</Table.Th>
            <Table.Th>Published</Table.Th>
            <Table.Th>By</Table.Th>
            <Table.Th>Note</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {versions.map((v, i) => (
            <Table.Tr key={v.uuid}>
              <Table.Td>
                v{v.version}
                {i === 0 && <Badge ml="xs" size="xs" variant="light" color="green">latest</Badge>}
              </Table.Td>
              <Table.Td>{v.publishedAt ? new Date(v.publishedAt).toLocaleString() : '—'}</Table.Td>
              <Table.Td>{v.publishedBy ?? '—'}</Table.Td>
              <Table.Td>{v.note ?? '—'}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <div className="admin-version-pointer" style={{ padding: '10px 12px' }}>{riskLink}</div>
    </div>
  )
}
